import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Sparkles, UserPlus, Users } from "lucide-react";
import { useAuth } from "../App";

/**
 * Redesigned empty state that shows a ghost-orbit preview of what the
 * user's circle *could* look like, with an emotional hook and social proof.
 */
const GHOST_PROMPTS = [
  { label: "A mentor", hint: "who's seen you at your best" },
  { label: "A friend", hint: "who'll tell you the truth" },
  { label: "A coworker", hint: "who sees what you miss" },
];

const NetworkEmptyState = ({ onInviteClick }) => {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let raf;
    let lastTs = 0;
    const step = (ts) => {
      if (!lastTs) lastTs = ts;
      if (ts - lastTs >= 50) {
        setTick((t) => t + 1);
        lastTs = ts;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Simple orbital animation for 3 ghost nodes
  const ghosts = GHOST_PROMPTS.map((p, i) => {
    const angle = ((i * 120 + tick * 0.3) * Math.PI) / 180;
    const rx = 150;
    const ry = 95;
    return {
      ...p,
      x: Math.cos(angle) * rx,
      y: Math.sin(angle) * ry,
    };
  });

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      style={{
        minHeight: 560,
        background:
          "radial-gradient(ellipse at center, #f8f7ff 0%, #f1f0fb 40%, #eeedf7 100%)",
      }}
      data-testid="network-empty-state"
    >
      {/* Top banner — emotional hook */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 pt-10 pb-4 text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-100/70 text-violet-700 text-xs font-medium mb-4 backdrop-blur">
          <Sparkles className="w-3.5 h-3.5" />
          Your circle is about to begin
        </div>
        <h2
          className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight max-w-xl mx-auto"
          style={{ fontFamily: "system-ui, sans-serif" }}
        >
          Who are the <span className="text-violet-600">3 people</span> whose
          honest take would change your year?
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
          Invite them to exchange insights on your SWOT, Values, and growth.
          They see what you can&apos;t.
        </p>
      </div>

      {/* Ghost orbital preview */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: 80 }}>
        <div className="relative" style={{ width: 420, height: 320 }}>
          {/* Dotted orbit rings */}
          <svg
            width="420"
            height="320"
            className="absolute inset-0 pointer-events-none"
          >
            <defs>
              <radialGradient id="empty-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="210" cy="160" r="80" fill="url(#empty-glow)" />
            <ellipse
              cx="210"
              cy="160"
              rx="150"
              ry="95"
              fill="none"
              stroke="#8b5cf6"
              strokeOpacity="0.28"
              strokeWidth="1.25"
              strokeDasharray="4 8"
            />
            {ghosts.map((g, i) => (
              <line
                key={`gl-${i}`}
                x1="210"
                y1="160"
                x2={210 + g.x}
                y2={160 + g.y}
                stroke="#8b5cf6"
                strokeOpacity="0.15"
                strokeWidth="0.8"
                strokeDasharray="3 5"
              />
            ))}
          </svg>

          {/* Center "You" node */}
          <div
            className="absolute flex flex-col items-center z-20"
            style={{ left: 210 - 40, top: 160 - 40, width: 80 }}
          >
            <div
              className="rounded-full overflow-hidden"
              style={{
                width: 80,
                height: 80,
                border: "3px solid #8b5cf6",
                boxShadow:
                  "0 0 0 4px rgba(139,92,246,0.12), 0 4px 24px rgba(139,92,246,0.15)",
              }}
            >
              <Avatar className="w-full h-full">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-violet-100 text-violet-600 text-xl font-bold">
                  {user?.name?.charAt(0) || "Y"}
                </AvatarFallback>
              </Avatar>
            </div>
            <span className="text-[11px] font-semibold text-slate-700 mt-1.5 bg-white/90 px-2 py-0.5 rounded-full shadow-sm border border-slate-100">
              You
            </span>
          </div>

          {/* Ghost nodes */}
          {ghosts.map((g, i) => (
            <div
              key={`ghost-${i}`}
              className="absolute z-10 flex flex-col items-center"
              style={{
                left: 210 + g.x - 28,
                top: 160 + g.y - 28,
                width: 56,
              }}
            >
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  width: 56,
                  height: 56,
                  border: "2px dashed #a78bfa",
                  backgroundColor: "rgba(167,139,250,0.08)",
                }}
              >
                <UserPlus className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-[10px] font-semibold text-slate-700 mt-1.5 leading-tight text-center">
                {g.label}
              </span>
              <span className="text-[9px] text-slate-400 leading-tight text-center max-w-[100px]">
                {g.hint}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA + social proof */}
      <div className="absolute bottom-0 left-0 right-0 z-30 px-6 pb-10 pt-4">
        <div className="max-w-md mx-auto text-center">
          <Button
            onClick={onInviteClick}
            size="lg"
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:-translate-y-0.5"
            data-testid="empty-state-invite-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite your first person
          </Button>
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <Users className="w-3.5 h-3.5" />
            <span>
              People with <span className="font-semibold text-slate-700">3+ insights</span> from their circle finish their SWOT 2.4× faster
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkEmptyState;
